import "./CrmEditor.css"
import CrmControls from "./CrmControls"
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { selectCurrentId, selectCurrentRole } from "../../auth/authSlice"
import { useDispatch } from "react-redux"
import { showSpinner, hideSpinner } from "../../../components/componentSlice"
import { useGetCrmDetailsMutation, useGetClientAndAccountsMutation, useUpdateClientAndAccountsMutation, useCreateClientAccountMutation, useDeleteClientMutation, useDeleteAccountMutation } from "./crmDataApiSlice"
import { useNavigate, useParams } from "react-router-dom"
import CrmInputs from "./CrmInputs"
import { message, Modal, Empty } from 'antd'
import ClientAccountCreator from "./ClientAccountCreator"
import { getCopyHouseHoldFieldList } from "../template/TemplateHelper"
import { downloadFile } from "../../utils/Utilities"
import { getFormDataSetFieldValue } from "./util/CrmFieldUtil"
import { useGetReportTemplatesMutation, useCreateReportMutation } from "../../report/reportApiSlice"
import { validateClientName, validateEmailAddress, validatePrimaryAddress } from "./util/CrmInputValidator"
import { useGetFieldOptionsMutation } from "../fieldOptionApiSlice"
import ClientDocument from "./ClientDocument"

const CrmEditor = () => {
    const { id } = useParams()
    const userId = useSelector(selectCurrentId)
    const role = useSelector(selectCurrentRole)

    const [messageApi, contextHolder] = message.useMessage()
    
    const [clients, setClients] = useState([])
    const [clientAccountsInfos, setClientAccountsInfos] = useState(null)
    const [selectedClient, setSelectedClient] = useState(null)
    const [broker, setBroker] = useState("Default")
    const [reportTemplates, setReportTemplates] = useState([])
    const [errorMessage, setErrorMessage] = useState(null)
    const [isCreateClientAccountModalOpen, setCreateClientAccountModalOpen] = useState(false)
    const [formErrorList, setFormErrorList] = useState([])
    const [isClientDocumentModalOpen, setClientDocumentModalOpen] = useState(false)
    const [fieldOptions, setFieldOptions] = useState(null)

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [getCrmDetails, { isLoading }] = useGetCrmDetailsMutation()
    const [getClientAndAccounts, { isLoading: isGetClientAndAccountsLoading }] = useGetClientAndAccountsMutation()
    const [updateClientAndAccounts, { isLoading: isUpdateClientAndAccountsLoading }] = useUpdateClientAndAccountsMutation()
    const [deleteClient, { isLoading: isDeleteClientLoading }] = useDeleteClientMutation()
    const [deleteAccount, { isLoading: isDeleteAccountLoading }] = useDeleteAccountMutation()
    const [createClientAccount, { isLoading: isCreateClientAccountLoading }] = useCreateClientAccountMutation()
    const [getReportTemplates, { isLoading: isGetReportTemplatesLoading }] = useGetReportTemplatesMutation()
    const [createReport,  { isLoading: isCreateReportLoading }] = useCreateReportMutation()
    const [getFieldOptions,  { isLoading: isGetFieldOptionsLoading }] = useGetFieldOptionsMutation()

    useEffect(() => {
        async function handleGetFileDetails() {
            try {
                const response = await getCrmDetails({crmId: id, userId: userId}).unwrap()
                if(response) {
                    setClients(response.clients)
                    setBroker(response.brokerType)
                    if(response.lastUpdatedClientId) {
                        selectLastUpdatedClient(response.lastUpdatedClientId, response.clients)
                    }
                }
            } catch(err) {
                if(err.status == 403) {
                    navigate("/dash/error403")
                } else {
                    setErrorMessage(err.data?.detail)
                }
            }
        }
        if(userId && id != 0) {
            handleGetFileDetails()
        }
    }, [userId])

    useEffect(() => {
        async function handleGetReportTemplates() {
            if(!id) {
                return
            }

            try {
                let request = {
                    userId: userId,
                    crmId: id
                }
                const response = await getReportTemplates(request).unwrap()
                if(response) {
                    setReportTemplates(response.templates)
                }
            } catch(err) {
                if(!err.status || err.status === "FETCH_ERROR") {
                    setErrorMessage("No Server Response")
                } else {
                    setErrorMessage(err.data?.detail)
                }
            }
        }
        if(role !== "EMPLOYEE") {
            handleGetReportTemplates()
        }
    }, [])
    
    useEffect(() => {
        async function handleGetFieldOptions() {
            try {
                const response = await getFieldOptions(broker).unwrap()
                if(response?.fieldOptions) {
                    setFieldOptions(response.fieldOptions)
                }
            } catch(err) {
                if(err.status == 403) {
                    navigate("/dash/error403")
                } else {
                    setErrorMessage(err.data?.detail)
                }
            }
        }
        if(broker) {
            handleGetFieldOptions()
        }
    }, [broker])

    useEffect(() => {
        if(isLoading || isGetClientAndAccountsLoading || isDeleteClientLoading || isDeleteAccountLoading || isCreateClientAccountLoading) {
            dispatch(showSpinner())
        } else{
            dispatch(hideSpinner())
        }
    }, [isLoading, isGetClientAndAccountsLoading, isDeleteClientLoading, isDeleteAccountLoading, isCreateClientAccountLoading])

    const getClientOptions = () => {
        return clients.map(client => ({id: client, name: client.firstName + " " + client.lastName}))
    }

    const handleSelectedClientChange = async (client) => {
        setErrorMessage(null)

        if(!isFormValid()) {
            return
        }

        handleUpdate(true, true)
        const response = await handleGetClientAndAccounts(client.clientId)
        if(response) {
            setClientAccountsInfos(response)
            setSelectedClient(client)
        }

    }

    const handleGetClientAndAccounts = async (clientId) => {
        try {
            let request = {
                crmId: id, 
                clientId: clientId,
                userId: userId
            }
            return await getClientAndAccounts(request).unwrap()
        } catch(err) {
            if(err.status == 403) {
                navigate("/dash/error403")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const handleRefreshClientAccountsInfo = async (clientId) => {
        const response = await handleGetClientAndAccounts(clientId)
        if(response) {
            setClientAccountsInfos(response)
        }
    }

    const showSuccessfulAlert = (message) => {
        messageApi.success(message)
    }

    const selectLastUpdatedClient = (lastUpdatedClientId, clientsDetails) => {
        let lastUpdatedClientDetails = clientsDetails.filter(client => client.clientId === lastUpdatedClientId)[0]

        if(lastUpdatedClientDetails) {
            handleSelectedClientChange(lastUpdatedClientDetails)
        }
    }

    const handleUpdate = async (isAutoUpdate, isFromSelectedClientChange) => {
        setErrorMessage(null)

        if(!isFormValid() || !clientAccountsInfos) {
            return
        }

        let request = {
            crmId: id,
            userId: userId,
            clientAndAccountsInfo: clientAccountsInfos
        }
        try {
            await updateClientAndAccounts(request).unwrap()

            const inputFirstName =  getFormDataSetFieldValue(clientAccountsInfos.clientFormDataSet, "First Name")
            const inputLastName =  getFormDataSetFieldValue(clientAccountsInfos.clientFormDataSet, "Last Name")
            const inputSsn =  getFormDataSetFieldValue(clientAccountsInfos.clientFormDataSet, "Social Security Number")
            updateSelectedClientDetails(inputFirstName, inputLastName, inputSsn)

            if(!isFromSelectedClientChange) {
                setSelectedClient((prevSelectedClient) => ({...prevSelectedClient, 
                    firstName: inputFirstName,
                    lastName: inputLastName,
                    socialSecurityNumber: inputSsn
                }))
            }
            
            if(!isAutoUpdate) {
                showSuccessfulAlert("Updated!")
            }
        } catch(err) {
            if(err.status == 403) {
                navigate("/dash/error403")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const updateSelectedClientDetails = (inputFirstName, inputLastName, inputSsn) => {
        let isSelectedClientDetailsUpdated =  (inputFirstName !== selectedClient.firstName ||
                                                inputLastName !== selectedClient.lastName ||
                                                inputSsn !== selectedClient.socialSecurityNumber)    

        if(!isSelectedClientDetailsUpdated) {
            return
        }

        let updatedClients = clients.map(client => {
            let updatedClient = client

            if(client.firstName === selectedClient.firstName &&
                client.lastName === selectedClient.lastName &&
                client.socialSecurityNumber === selectedClient.socialSecurityNumber) {
                    updatedClient = {...updatedClient,
                        firstName: inputFirstName,
                        lastName: inputLastName,
                        socialSecurityNumber: inputSsn
                    }
                }
            return updatedClient
        })
        setClients(updatedClients)
    }

    const handleDeleteClient = async (clientId) => {
        setErrorMessage(null)
        try {
            let request = {
                clientId
            }
            await deleteClient(request).unwrap()
            setClientAccountsInfos(null)
            setSelectedClient(null)
            removeClientFromClientSelector(clientId)

            showSuccessfulAlert("Deleted!")
        } catch(err) {
            if(err.status == 403) {
                navigate("/dash/error403")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const handleDeleteAccount = async (accountId, clientId) => {
        setErrorMessage(null)
        try {
            let request = {
                accountId,
                clientId
            }
            await deleteAccount(request).unwrap()
            removeAccountFromClientAccountsInfos(accountId, clientId)
            showSuccessfulAlert("Deleted!")
        } catch(err) {
            if(err.status == 403) {
                navigate("/dash/error403")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const removeClientFromClientSelector = (clientId) => {
        let filteredClients = clients.filter(client => client.clientId !== clientId)
        setClients(filteredClients)
    }

    const removeAccountFromClientAccountsInfos = (accountId, clientId) => {
        let filteredAccounts = clientAccountsInfos.accounts.filter(account => account.accountId !== accountId)
        if(filteredAccounts.length > 0) {
            setClientAccountsInfos(prev => ({...prev, accounts: filteredAccounts}))
        } else {
            setClientAccountsInfos(null)
            setSelectedClient(null)
            removeClientFromClientSelector(clientId)
        }
    }

    const handleCreateClientAccount = async (clientDetails, accountPlatformAndTypeList) => {
        setErrorMessage(null)

        let existingClient = clients.filter(client => client.firstName?.trim() === clientDetails.firstName?.trim() &&
            client.lastName?.trim() === clientDetails.lastName?.trim() &&
            client.socialSecurityNumber?.trim() === clientDetails.socialSecurityNumber?.trim())[0]

        let requestClient = existingClient ? existingClient : clientDetails

        try {
            let request = {
                crmId: id,
                userId: userId,
                clientDetails: requestClient,
                accountPlatformAndTypeList: accountPlatformAndTypeList
            }
            const response = await createClientAccount(request).unwrap()
            if(response) {
                let updatedClientAccountsInfo
                if(existingClient) {
                    updatedClientAccountsInfo = ({...clientAccountsInfos, accounts: [...clientAccountsInfos.accounts, ...response.accounts]})
                } else {
                    updatedClientAccountsInfo =  {...response}

                    let createdClient = {...clientDetails, clientId: response.clientId}
                    setSelectedClient(createdClient)
                    setClients((prevClients) => ([...prevClients, createdClient]))
                }
                setClientAccountsInfos(updatedClientAccountsInfo)
                setCreateClientAccountModalOpen(false)
                showSuccessfulAlert("Created!")
            }
        } catch(err) {
            if(err.status == 403) {
                navigate("/dash/error403")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const handleCopyHouseHold = async (client) => {
        setErrorMessage(null)
        const response = await handleGetClientAndAccounts(client.clientId)
        if(response) {
            copyHouseHold(response)
            showSuccessfulAlert("Copied!")
        }
    }

    const copyHouseHold = (toBeCopiedClient) => {
        const copyHouseHoldFields = getCopyHouseHoldFieldList(broker)

        let updatedClientFormDataSet = clientAccountsInfos.clientFormDataSet.map(formData => {
            if(copyHouseHoldFields.includes(formData.formFieldName)) {
                return {...formData, formFieldValue: getFormDataSetFieldValue(toBeCopiedClient.clientFormDataSet, formData.formFieldName)}
            } else {
                return formData
            }
        })

        setClientAccountsInfos(prev => ({...prev, clientFormDataSet: updatedClientFormDataSet}))
    }

    const handleGenerateReport = async (template) => {
        if(!template) {
            return
        }

        try {
            const data = await createReport({id: template.id, userId: userId}).unwrap()

            downloadFile(data, template.templateName + ".xlsx")
        } catch(err) {
            if(err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response");
            } else if(err.status === 400) {
                setErrorMessage("Error exporting file");
            } else if(err.status === 401) {
                setErrorMessage("Unauthorized");
            } else {
                setErrorMessage(err.data?.message);
            }
        }
    }

    const isFormValid = () => {
        if(clientAccountsInfos) {
            let updatedFormErrorList = []
            let clientInfo = clientAccountsInfos.clientFormDataSet

            validateClientName(getFormDataSetFieldValue(clientInfo, "First Name"), getFormDataSetFieldValue(clientInfo, "Last Name"), updatedFormErrorList)
            validateEmailAddress(getFormDataSetFieldValue(clientInfo, "Email Address"), updatedFormErrorList)
            validatePrimaryAddress(getFormDataSetFieldValue(clientInfo, "Primary Address"), updatedFormErrorList)
            setFormErrorList(updatedFormErrorList)
            
            return updatedFormErrorList && updatedFormErrorList.length == 0
        }
        return true
    }

    return (
        <div className="file-details-page-container">
            {contextHolder}
            {clients?.length > 0 ? 
                <>
                    {errorMessage && 
                        <div className="error-message">
                            <p>{errorMessage}</p>
                        </div>
                    }
                    <CrmControls clientOptions={getClientOptions()}
                                        handleSelectedClientChange={handleSelectedClientChange} 
                                        selectedClient={selectedClient} broker={broker} 
                                        setCreateClientAccountModalOpen={setCreateClientAccountModalOpen}
                                        setClientDocumentModalOpen={setClientDocumentModalOpen}
                                        handleCopyHouseHold={handleCopyHouseHold}
                                        handleGenerateReport={handleGenerateReport}
                                        isCreateReportLoading={isCreateReportLoading}
                                        reportTemplates={reportTemplates}
                    />
                    <CrmInputs  crmId={id}            
                                        clientAccountsInfos={clientAccountsInfos}
                                        clientOptions={getClientOptions()} 
                                        broker={broker} 
                                        setClientAccountsInfos={setClientAccountsInfos}
                                        handleUpdate={handleUpdate}
                                        selectedClient={selectedClient}
                                        isUpdating={isUpdateClientAndAccountsLoading}
                                        handleDeleteClient={handleDeleteClient}
                                        handleDeleteAccount={handleDeleteAccount}
                                        formErrorList={formErrorList}
                                        refreshClientAccountsInfo={handleRefreshClientAccountsInfo}
                                        fieldOptions={fieldOptions}
                    />
                    <Modal open={isCreateClientAccountModalOpen} onCancel={() => setCreateClientAccountModalOpen(false)} footer={[]}>
                        <ClientAccountCreator
                            broker={broker} 
                            selectedClient={selectedClient}
                            handleCreateClientAccount={handleCreateClientAccount}
                        />
                    </Modal>

                    <ClientDocument 
                        client={selectedClient}
                        openModal={isClientDocumentModalOpen}
                        handleModalOpen={setClientDocumentModalOpen}
                    />
                </>
                :
                <div className="no-file">
                    <Empty description={errorMessage ? errorMessage : "No information yet."} />
                </div>
            }
        </div>
    )
}

export default CrmEditor